import { Component } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { whitespaceValidator } from '@vendasta/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PipelineSettingsService } from '../pipeline-settings.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Pipeline, Stage, StandardIds } from '@galaxy/crm/static';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, firstValueFrom, Observable, of, take } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { StageData, StageMap } from '../interfaces';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInput } from '@angular/material/input';
import { MatCard, MatCardContent, MatCardActions } from '@angular/material/card';
import { MatDivider } from '@angular/material/divider';
import { NgClass, AsyncPipe } from '@angular/common';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { GalaxyStickyFooterModule } from '@vendasta/galaxy/sticky-footer';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';

const probabilityValidator: ValidatorFn = (control: AbstractControl) => {
  const formArray = control as FormArray;
  const probabilityMap = new Map<number, FormControl[]>();
  for (const control of formArray.controls) {
    const probabilityControl = control.get('probability') as FormControl;
    const probability = probabilityControl?.value || 0;
    if (!probabilityMap.has(probability)) {
      probabilityMap.set(probability, []);
    }
    probabilityMap.get(probability)?.push(probabilityControl);
  }

  let duplicateFound = false;
  for (const controls of probabilityMap.values()) {
    if (controls.length > 1) {
      controls.forEach((c) => {
        c.setErrors({ ...c.errors, duplicate: true });
        c.markAsTouched();
      });
      duplicateFound = true;
    } else {
      const c = controls[0];
      if (c.errors && 'duplicate' in c.errors) {
        delete c.errors['duplicate'];
        c.setErrors(Object.keys(c.errors).length ? c.errors : null);
        c.markAsTouched();
      }
    }
  }

  return duplicateFound ? { duplicate: true } : null;
};

@Component({
  selector: 'crm-pipeline-settings-form',
  templateUrl: './pipeline-settings-form.component.html',
  styleUrls: ['./pipeline-settings-form.component.scss'],
  imports: [
    GalaxyLoadingSpinnerModule,
    FormsModule,
    ReactiveFormsModule,
    GalaxyFormFieldModule,
    MatInput,
    MatCard,
    MatDivider,
    MatCardContent,
    NgClass,
    MatIconButton,
    MatIcon,
    MatCardActions,
    MatButton,
    GalaxyStickyFooterModule,
    GalaxyButtonLoadingIndicatorModule,
    AsyncPipe,
    TranslateModule,
  ],
})
export class PipelineSettingsFormComponent {
  protected readonly pipelineForm = new FormGroup({
    stages: new FormArray<FormGroup>([], {
      validators: probabilityValidator,
    }),
    name: new FormControl('', {
      validators: whitespaceValidator(),
      nonNullable: true,
    }),
  });

  protected readonly currentPipeline$ = this.pipelineSettingsService.currentPipeline$;
  protected readonly fallbackPipelineId$ = this.pipelineSettingsService.fallbackPipelineId$;
  protected readonly loading$ = this.pipelineSettingsService.loadingPipeline$;
  protected readonly savingChanges$$ = new BehaviorSubject(false);
  protected readonly pendingChanges$$ = new BehaviorSubject(false);

  constructor(
    private readonly pipelineSettingsService: PipelineSettingsService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly snackbarService: SnackbarService,
    private readonly confirmationModal: OpenConfirmationModalService,
    private readonly translateService: TranslateService,
  ) {
    this.pipelineForm.valueChanges.pipe(takeUntilDestroyed()).subscribe((_) => this.pendingChanges$$.next(true));
    this.activatedRoute.params.pipe(takeUntilDestroyed()).subscribe((params) => {
      if (params['pipelineId']) {
        this.pipelineSettingsService.setPipelineId(params['pipelineId']);
      } else {
        this.loadFallbackPipeline();
      }
    });

    this.currentPipeline$.pipe(takeUntilDestroyed()).subscribe((pipeline) => {
      if (pipeline?.id) {
        this.setFormValues(pipeline);
      }
    });
  }

  protected get stages(): FormArray<FormGroup> {
    return this.pipelineForm.controls.stages;
  }

  protected keyIsInteger(event: KeyboardEvent): boolean {
    return /^[0-9]$/.test(event.key);
  }

  protected addStage(): void {
    const lastOpenStageIndex = this.stages.controls.map((s) => s.get('status')?.value).lastIndexOf('Open');
    const nextProbability = (this.stages.controls[lastOpenStageIndex]?.get('probability')?.value || 0) + 1;
    const newStage = new FormGroup({
      name: new FormControl('', whitespaceValidator()),
      probability: new FormControl(nextProbability, [Validators.required, Validators.min(1), Validators.max(99)]),
      status: new FormControl('Open'),
    });
    this.stages.insert(lastOpenStageIndex + 1, newStage);
    newStage.markAllAsTouched();
    newStage.get('name')?.markAsDirty();
    newStage.get('probability')?.markAsDirty();
  }

  private setFormValues(pipeline: Pipeline) {
    this.pipelineForm.reset({ name: pipeline.name }, { emitEvent: false });
    this.stages.clear();
    let closedWonExists = false;
    let closedLostExists = false;
    pipeline.stages.forEach((stage) => {
      const idCtrl = new FormControl(stage.id);
      const nameCtrl = new FormControl(stage.name, whitespaceValidator());
      const stageProbability = this.getStageProbability(stage);
      const probabilityCtrl = new FormControl(this.getStageProbability(stage));
      const statusCtrl = new FormControl(this.getStageStatus(stage));

      if (stageProbability === 0) {
        closedLostExists = true;
        probabilityCtrl.disable();
      } else if (stageProbability === 100) {
        closedWonExists = true;
        probabilityCtrl.disable();
      } else {
        probabilityCtrl.setValidators([Validators.required, Validators.min(1), Validators.max(99)]);
      }
      const newStage = new FormGroup({
        id: idCtrl,
        name: nameCtrl,
        probability: probabilityCtrl,
        status: statusCtrl,
      });
      this.stages.push(newStage, { emitEvent: false });
    });
    this.pendingChanges$$.next(false);

    // add Closed stages if they don't exist in the current settings
    if (!closedWonExists) {
      const nameCtrl = new FormControl(
        this.translateService.instant('PIPELINES.SETTINGS.DEFAULTS.CLOSED_WON'),
        whitespaceValidator(),
      );
      const probabilityCtrl = new FormControl(100);
      const statusCtrl = new FormControl('Closed Won');
      probabilityCtrl.disable();
      const newStage = new FormGroup({
        name: nameCtrl,
        probability: probabilityCtrl,
        status: statusCtrl,
      });
      this.stages.push(newStage);
    }
    if (!closedLostExists) {
      const nameCtrl = new FormControl(
        this.translateService.instant('PIPELINES.SETTINGS.DEFAULTS.CLOSED_LOST'),
        whitespaceValidator(),
      );
      const probabilityCtrl = new FormControl(0);
      const statusCtrl = new FormControl('Closed Lost');
      probabilityCtrl.disable();
      const newStage = new FormGroup({
        name: nameCtrl,
        probability: probabilityCtrl,
        status: statusCtrl,
      });
      this.stages.push(newStage);
    }
  }

  private patchFormValues(pipeline: Pipeline) {
    for (let i = 0; i < this.stages.length; i++) {
      const stageGroup = this.stages.at(i);
      const stage = pipeline.stages.find((s) => this.getStageProbability(s) === stageGroup.get('probability')?.value);
      if (stage) {
        if (stage.id && !stageGroup.get('id')) {
          stageGroup.setControl('id', new FormControl(''));
        }
        stageGroup.patchValue({
          id: stage?.id || '',
          name: stage?.name,
          probability: this.getStageProbability(stage),
          status: this.getStageStatus(stage),
        });
      }
    }
  }

  private getStageProbability(stage: Stage) {
    const mutationFloat = stage.mutations.find(
      (mutation) => mutation.fieldId === StandardIds.OpportunityProbability,
    )?.floatValue;
    return mutationFloat ? Math.round(mutationFloat * 100) : 0;
  }

  private getStageStatus(stage: Stage): string {
    const mutationValue = stage.mutations.find(
      (mutation) => mutation.fieldId === StandardIds.OpportunityStatus,
    )?.stringValue;
    return mutationValue ?? 'Open';
  }

  private loadFallbackPipeline() {
    this.fallbackPipelineId$
      .pipe(
        filter((id) => !!id),
        take(1),
      )
      .subscribe((fallbackPipelineId) => {
        this.router.navigate([`${fallbackPipelineId}`], {
          relativeTo: this.activatedRoute,
        });
      });
  }

  protected saveChanges(pipelineId: string) {
    if (this.pipelineForm.valid && this.pipelineForm.dirty) {
      this.savingChanges$$.next(true);
      const pipelineName = this.pipelineForm.controls.name.value ?? '';
      const stageProbabilities: StageMap = new Map<string, StageData>(
        this.stages.controls.map((stage) => [
          stage.get('name')?.value,
          {
            id: stage.get('id')?.value || '',
            probability: stage.get('probability')?.value / 100,
            status: stage.get('status')?.value,
          },
        ]),
      );
      this.pipelineSettingsService
        .updatePipeline$('Opportunity', pipelineId, pipelineName, stageProbabilities)
        .subscribe({
          next: (pipeline) => {
            this.patchFormValues(pipeline);
            this.savingChanges$$.next(false);
            this.pendingChanges$$.next(false);
            this.pipelineForm.markAsPristine();
            this.snackbarService.openSuccessSnack('PIPELINES.SETTINGS.SNACKBARS.SAVE_SUCCESS');
          },
          error: () => {
            this.savingChanges$$.next(false);
            this.snackbarService.openErrorSnack('PIPELINES.SETTINGS.SNACKBARS.SAVE_FAILED');
          },
        });
    } else if (this.pipelineForm.dirty) {
      this.snackbarService.openErrorSnack('PIPELINES.SETTINGS.SNACKBARS.FORM_INVALID');
    }
  }

  deleteDisplayStage(index: number) {
    this.stages.removeAt(index);
    this.pipelineForm.markAsDirty();
  }

  async delete(pipelineId: string): Promise<void> {
    const proceed = await firstValueFrom(
      this.confirmationModal.openModal({
        type: 'warn',
        title: 'PIPELINES.SETTINGS.MODALS.DELETE_TITLE',
        message: 'PIPELINES.SETTINGS.MODALS.DELETE_MESSAGE',
        confirmButtonText: 'ACTIONS.DELETE',
        cancelButtonText: 'ACTIONS.CANCEL',
        actionOnEnterKey: false,
      }),
    );
    if (proceed) {
      const success = await firstValueFrom(this.pipelineSettingsService.deletePipeline$(pipelineId));
      if (success) {
        this.snackbarService.openSuccessSnack('PIPELINES.SETTINGS.SNACKBARS.DELETE_SUCCESS');
        await this.router.navigate(['../'], { relativeTo: this.activatedRoute });
      } else {
        this.snackbarService.openErrorSnack('PIPELINES.SETTINGS.SNACKBARS.DELETE_FAILED');
      }
    }
  }

  protected async cancel() {
    const pipeline = await firstValueFrom(this.currentPipeline$);
    if (pipeline?.id) {
      this.setFormValues(pipeline);
    }
  }

  canDeactivate(): Observable<boolean> {
    return this.pendingChanges$$.pipe(
      switchMap((pendingChanges) => {
        if (pendingChanges) {
          return this.confirmationModal
            .openModal({
              type: 'warn',
              title: 'PIPELINES.SETTINGS.MODALS.UNSAVED_CHANGES_TITLE',
              message: 'PIPELINES.SETTINGS.MODALS.UNSAVED_CHANGES_MESSAGE',
              confirmButtonText: 'PIPELINES.SETTINGS.MODALS.UNSAVED_CHANGES_CONFIRM',
            })
            .pipe(map((confirmation) => !!confirmation));
        } else {
          return of(true);
        }
      }),
    );
  }
}
